/* Vocal range test: find the lowest and highest comfortable notes, then suggest a voice type. */
import { useEffect, useRef, useState } from 'react';
import { useGo } from '../../app/navigation';
import { isMicOn, startMic, useMicFrame } from '../../core/mic';
import { addXp, award, setSetting, useSettings } from '../../core/store';
import { noteName } from '../../core/theory';
import { toast } from '../../core/toast';
import { fmtDec } from '../../core/util';
import { Btn } from '../../ui/Button';
import { Icon } from '../../ui/icons';
import { Rich } from '../../ui/Rich';
import { modStyle } from '../../ui/style';
import { MicGate } from '../exercise/MicBlock';
import { VoicePicker } from './VoicePicker';
import { rangeText, VOICE_TYPES, type VoiceType } from './voice';

type Step = 'lo' | 'hi';

function guess(lo: number, hi: number): VoiceType {
  let best: { v: VoiceType; sc: number } | null = null;
  for (const v of VOICE_TYPES) {
    const ov = Math.min(hi, v.hi) - Math.max(lo, v.lo);
    const miss = Math.max(0, v.lo - lo) + Math.max(0, hi - v.hi);
    const center = Math.abs((lo + hi) / 2 - (v.lo + v.hi) / 2);
    const sc = ov - center * 0.6 - miss * 0.2;
    if (!best || sc > best.sc) best = { v, sc };
  }
  return (best as { v: VoiceType }).v;
}

export function RangeTest() {
  const go = useGo();
  useSettings();
  const [step, setStep] = useState<Step | null>(null);
  const [res, setRes] = useState<{ lo: number | null; hi: number | null }>({ lo: null, hi: null });
  const liveNote = useRef<HTMLDivElement>(null);
  const liveDet = useRef<HTMLDivElement>(null);
  const stable = useRef<{ m: number | null; since: number }>({ m: null, since: 0 });
  const resultEl = useRef<HTMLDivElement>(null);
  const stepRef = useRef(step);
  stepRef.current = step;

  useMicFrame((f) => {
    if (!f.voiced || f.clarity < 0.85 || f.midi == null) {
      if (liveNote.current) liveNote.current.textContent = '—';
      if (liveDet.current) liveDet.current.textContent = 'Bir nota söyle';
      stable.current.m = null;
      return;
    }
    const m = Math.round(f.midi);
    if (liveNote.current) liveNote.current.textContent = noteName(m);
    if (liveDet.current) liveDet.current.textContent = `${fmtDec(f.freq, 1)} Hz`;
    if (Math.abs(f.midi - m) > 0.45) {
      stable.current.m = null;
      return;
    }
    if (m !== stable.current.m) {
      stable.current = { m, since: f.t };
      return;
    }
    const st = stepRef.current;
    if (f.t - stable.current.since >= 0.5 && st) {
      setRes((r) => {
        if (st === 'lo' && (r.lo == null || m < r.lo)) return { ...r, lo: m };
        if (st === 'hi' && (r.hi == null || m > r.hi)) return { ...r, hi: m };
        return r;
      });
    }
  });

  // once a step ends with both notes found, the result card (below the steps) comes into view
  const hasResult = res.lo != null && res.hi != null && res.hi > res.lo;
  useEffect(() => {
    if (step === null && hasResult) resultEl.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [step, hasResult]);

  const toggle = async (which: Step) => {
    if (step === which) {
      setStep(null);
      return;
    }
    if (!isMicOn()) {
      const ok = await startMic();
      if (!ok) {
        toast('Mikrofon açılamadı.', { tone: 'bad' });
        return;
      }
    }
    setStep(which);
    setRes((r) => ({ ...r, [which]: null }));
  };

  const box = (which: Step) => {
    const val = res[which];
    const active = step === which;
    return (
      <div className={`range-box${active ? ' active' : ''}`}>
        <div className="eyebrow">{which === 'lo' ? '1. adım' : '2. adım'}</div>
        <h3>{which === 'lo' ? 'En kalın rahat notan' : 'En ince rahat notan'}</h3>
        <p className="mb-note">
          {which === 'lo'
            ? 'Konuşma sesinden başla, yavaşça aşağı kay. Zorlamadan tutabildiğin en kalın notada bir saniye kal.'
            : 'Orta bir notadan yavaşça yukarı kay. Bağırmadan, rahat tutabildiğin en ince notada bir saniye kal.'}
        </p>
        <div className="range-val">{val != null ? noteName(val) : '—'}</div>
        <Btn kind={active ? 'primary' : 'ghost'} icon={active ? 'check' : 'mic'} onClick={() => toggle(which)}>
          {active ? 'Bitir' : val != null ? 'Tekrarla' : 'Başla'}
        </Btn>
      </div>
    );
  };

  let result = null;
  if (res.lo != null && res.hi != null && res.hi > res.lo) {
    const { lo, hi } = res;
    const span = hi - lo;
    const vt = guess(lo, hi);
    result = (
      <div className="ex-start-card range-result" ref={resultEl}>
        <div className="eyebrow">Sonuç</div>
        <h2>
          {noteName(lo)} – {noteName(hi)}
        </h2>
        <p>
          <Rich
            text={`Aralığın ${span} yarım ses (yaklaşık ${fmtDec(span / 12, 1)} oktav). Bu aralık en çok **${vt.name}** ses tipine yakın.`}
          />
        </p>
        <p className="mb-note">
          Ses tipi yalnızca aralıkla değil, sesin tınısı ve geçiş bölgeleriyle de belirlenir. Bu sonuç bir başlangıç
          noktası; bir ses öğretmeni daha kesin değerlendirir.
        </p>
        <div className="ex-start-actions">
          <Btn
            kind="primary"
            icon="check"
            onClick={() => {
              setSetting('voiceLow', lo);
              setSetting('voiceHigh', hi);
              setSetting('voiceType', vt.id);
              award('aralik_testi');
              addXp(10, 'vokal');
              toast('Ses aralığın kaydedildi.', { tone: 'good', icon: 'check' });
              go('vokal.antrenman');
            }}
          >
            Kaydet ve egzersizlere geç
          </Btn>
        </div>
      </div>
    );
  }

  return (
    <div className="ex-page" style={modStyle('vokal')}>
      <button type="button" className="link-back" onClick={() => go('vokal')}>
        <Icon name="left" />
        <span>Vokal &amp; Şan</span>
      </button>
      <div className="ex-start-card">
        <div className="eyebrow">Araç</div>
        <h1 className="ex-start-title">Ses aralığı testi</h1>
        <p className="ex-start-desc">
          En kalın ve en ince rahat notanı bulalım. Zorlama: sesin çatallaşıyor ya da gırtlağın sıkışıyorsa o nota
          sınırın sayılmaz.
        </p>
        <p className="mb-note">Şu anki kayıt: {rangeText()}</p>
      </div>
      <MicGate text="Aralık testi için mikrofon gerekir." />
      <div className="ms-live">
        <div className="ms-note" ref={liveNote}>
          —
        </div>
        <div className="ms-detail" ref={liveDet}>
          Mikrofon açıldığında sesini burada göreceksin
        </div>
      </div>
      <div className="range-steps">
        {box('lo')}
        {box('hi')}
      </div>
      {result}
      <div className="ex-start-card">
        <h2>Ya da ses tipini kendin seç</h2>
        <VoicePicker test={false} onDone={() => toast('Ses tipi kaydedildi.', { tone: 'good' })} />
      </div>
    </div>
  );
}
